/** Derives a contract's activity timeline from the data we already fetch —
 *  lifecycle stamps, invoices, indexations and the deposit — so the Activity
 *  tab and the Overview's recent-activity preview share one source of truth.
 *  (A dedicated events table can replace this later without changing callers.) */
import type { components } from "@/types/api";
import { money } from "@/lib/money";

type Contract = components["schemas"]["ContractResponse"];
type Invoice = components["schemas"]["InvoiceListItem"];
type Indexation = components["schemas"]["IndexationResponse"];
type Deposit = components["schemas"]["DepositResponse"];
type CostAdjustment = components["schemas"]["CostAdjustmentResponse"];
type Requirement = components["schemas"]["RequirementResponse"];

export type TimelineKind =
  | "created"
  | "signed"
  | "activated"
  | "ended"
  | "closed"
  | "invoice"
  | "indexation"
  | "cost_adjustment"
  | "deposit_received"
  | "deposit_settled";

export interface TimelineEvent {
  kind: TimelineKind;
  date: string; // YYYY-MM-DD
  title: string;
  detail?: string;
}

function day(v: string | null | undefined): string | null {
  return v ? v.slice(0, 10) : null;
}

export function buildTimeline(
  contract: Contract,
  invoices: Invoice[],
  indexations: Indexation[],
  deposit: Deposit | null,
  costAdjustments: CostAdjustment[] = [],
  requirements: Requirement[] = [],
): TimelineEvent[] {
  const ev: TimelineEvent[] = [];
  const created = day(contract.created_at);

  if (created) ev.push({ kind: "created", date: created, title: "Contract created" });
  if (contract.signed_date)
    ev.push({ kind: "signed", date: contract.signed_date, title: "Signed" });
  const activated = day(contract.activated_at);
  if (activated)
    ev.push({
      kind: "activated",
      date: activated,
      title: "Activated",
      detail: "Terms locked · billing can run",
    });
  if (contract.end_date && (contract.status === "ending" || contract.status === "closed"))
    ev.push({ kind: "ended", date: contract.end_date, title: "End date set" });
  const closed = day(contract.closed_at);
  if (closed) ev.push({ kind: "closed", date: closed, title: "Closed" });

  for (const inv of invoices) {
    if (!inv.issue_date) continue; // unissued drafts aren't timeline-worthy yet
    ev.push({
      kind: "invoice",
      date: inv.issue_date,
      title: `Invoice ${inv.number ?? "—"} · ${money(inv.total)}`,
      detail: inv.status,
    });
  }
  for (const ix of indexations) {
    ev.push({
      kind: "indexation",
      date: ix.effective_date,
      title: `Rent indexed +${ix.percentage}% → ${money(ix.new_rent)}`,
      detail: ix.method === "cpi" ? "CPI" : "Fixed",
    });
  }
  for (const ca of costAdjustments) {
    ev.push({
      kind: "cost_adjustment",
      date: ca.effective_date,
      title: `Service costs adjusted ${money(ca.old_monthly)} → ${money(ca.new_monthly)}`,
      detail: ca.note ?? undefined,
    });
  }
  if (deposit?.received_date)
    ev.push({
      kind: "deposit_received",
      date: deposit.received_date,
      title: `Deposit ${money(deposit.amount)} received`,
    });
  if (deposit && deposit.status === "closed") {
    const date = day(deposit.refunded_at) ?? deposit.received_date ?? created;
    if (date)
      ev.push({
        kind: "deposit_settled",
        date,
        title: "Deposit settled",
        detail:
          deposit.refund_to_tenant != null
            ? `Refunded ${money(deposit.refund_to_tenant)} to tenant`
            : undefined,
      });
  }

  for (const r of requirements) {
    if (r.mode === "sign" && r.signed_at) {
      const d = day(r.signed_at);
      if (d)
        ev.push({
          kind: "signed",
          date: d,
          title: `${r.subject} signed`,
          detail: r.signed_by ? `by ${r.signed_by}` : undefined,
        });
    }
  }

  return ev
    .filter((e) => e.date)
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}
